import React from 'react';
import { Clock, AlertTriangle, TrendingUp, Wallet } from 'lucide-react';
import { Button } from './ui/button';
import { formatAddress, calculateAPY } from '../utils/contracts';
import { format } from 'date-fns';

export const InvoiceCard = ({ invoice, onFinance, onViewDetails, userType = 'investor' }) => {
  const getRiskColor = (risk) => {
    if (risk <= 3) return 'text-emerald-600 border-emerald-200 bg-emerald-50';
    if (risk <= 6) return 'text-amber-600 border-amber-200 bg-amber-50';
    return 'text-red-600 border-red-200 bg-red-50';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'LISTED':
        return 'text-blue-600 border-blue-200 bg-blue-50';
      case 'FUNDED':
        return 'text-amber-600 border-amber-200 bg-amber-50';
      case 'PAID':
        return 'text-emerald-600 border-emerald-200 bg-emerald-50';
      case 'DEFAULTED':
        return 'text-red-600 border-red-200 bg-red-50';
      default:
        return 'text-slate-600 border-slate-200 bg-slate-50';
    }
  };

  const apy = calculateAPY(invoice.risk_score || invoice.riskScore);
  const amount = invoice.amount;
  const dueDate = invoice.due_date || invoice.dueDate;

  return (
    <div
      data-testid={`invoice-card-${invoice.id || invoice.token_id}`}
      className="professional-card p-6 card-hover"
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Invoice #{invoice.invoice_number || (invoice.token_id || invoice.id).toString().slice(0, 8)}
              </span>
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {invoice.company_name || 'Business Invoice'}
            </h3>
          </div>
          <div className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide border ${getStatusColor(invoice.status)}`}>
            {invoice.status || 'PENDING'}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 py-4 border-t border-b border-slate-200">
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Amount</div>
            <div className="text-2xl font-bold text-slate-900 font-mono">
              {typeof amount === 'number' ? amount.toFixed(2) : amount} ETH
            </div>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">APY</div>
            <div className="text-2xl font-bold text-indigo-600">{apy}%</div>
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2 text-slate-600">
            <Clock className="h-4 w-4" />
            <span className="font-medium">Due: {dueDate ? format(new Date(dueDate), 'MMM dd, yyyy') : 'N/A'}</span>
          </div>
          <div className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide border ${getRiskColor(invoice.risk_score || invoice.riskScore)}`}>
            <AlertTriangle className="h-3 w-3" />
            Risk {invoice.risk_score || invoice.riskScore}/10
          </div>
        </div>

        {userType === 'msme' && (
          <div className="flex items-center gap-2 text-xs text-slate-600 pt-2 border-t border-slate-100">
            <Wallet className="h-3 w-3" />
            <span className="font-mono">Debtor: {formatAddress(invoice.debtor_address || invoice.debtorAddress)}</span>
          </div>
        )}

        <div className="flex gap-2 mt-2">
          {onViewDetails && (
            <Button
              data-testid={`view-details-button-${invoice.id || invoice.token_id}`}
              onClick={() => onViewDetails(invoice)}
              variant="outline"
              className="flex-1 font-semibold border-slate-300 hover:border-indigo-600 hover:text-indigo-600"
            >
              View Details
            </Button>
          )}
          {onFinance && invoice.status === 'LISTED' && userType === 'investor' && (
            <Button
              data-testid={`finance-button-${invoice.id || invoice.token_id}`}
              onClick={() => onFinance(invoice)}
              className="flex-1 gradient-bg text-white hover:opacity-90 font-semibold button-scale shadow-lg shadow-indigo-500/30"
            >
              <TrendingUp className="mr-2 h-4 w-4" />
              Finance
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};